'use client';

import { columnTooltip, type ResolvedColumn } from '@/shared/mobile-service/lib/columns';
import { ARTIFACT_ID_FIELD } from '@/shared/mobile-service/lib/constants';
import { CellValue } from './renderers';

/**
 * Compact read-only sub-table for nested child artifacts (e.g. a call's
 * ``parties`` or a chat's participants). Rendered inside an expanded parent row
 * from the ``front_end.nested`` config; no paging/sorting — child collections
 * are small and returned inline with the parent.
 */
export function NestedTable({
  label,
  columns,
  rows,
}: {
  label: string;
  columns: ResolvedColumn[];
  rows: Record<string, unknown>[];
}) {
  if (rows.length === 0) {
    return <div className="px-4 py-2 text-xs text-white/50">No {label.toLowerCase()}.</div>;
  }

  return (
    <div className="px-4 py-3">
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-white/65">
        {label} ({rows.length})
      </div>
      <div className="overflow-hidden rounded border border-white/10">
        <table className="w-full text-left text-xs">
          <thead className="bg-white/5 text-white/65">
            <tr>
              {columns.map((c) => (
                <th key={c.field} className="px-3 py-1.5 font-medium" title={columnTooltip(c)}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((row, i) => (
              <tr key={(row[ARTIFACT_ID_FIELD] as number) ?? i}>
                {columns.map((c) => (
                  <td
                    key={c.field}
                    className={`px-3 py-1.5 ${c.align === 'right' ? 'text-right' : ''}`}
                  >
                    <CellValue col={c} value={row[c.field]} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
